import moment from "moment";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import { Complaintmodel } from "../../../../service/models/complaint_model";
import {
  GetComplaint,
  GetComplaintCreationDetailsByMobile,
  GetOrgByDeviceId,
  UpdateComplaint,
} from "../../../../service/repos/support/complaint_repo";
import { DeviceIdRegex } from "../../../../utils/constant";
import { getDayAgo } from "../../../../utils/date_util";
import {
  ACTION,
  complaintStatus,
  homeInstallationComplaintStatus,
  DEALER_SERVICE_RELATED_TO_OPTIONS,
  complaint_by,
  MODULE,
  PriorityType,
  SubDepartments,
  ComplaintTicketType,
  HomeInstallTicketType,
  SourceType,
  ComplaintsRelatedToWithSubCategory,
} from "../../../../utils/enums";
import LoadingWidget from "../../../components/loading_spinner";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../new_components/common/drawer_modal";
import ModalDialog from "../../../new_components/common/modal";
import EmployeeSearchWidget from "../../../new_components/emp_search_widget";
import CommentWidget from "../listing/components/comment_widget";
import HistoryWidget from "../listing/components/history_widget";
import AttachmentWidget from "./components/attaments_widget";
import ReplacementPipelineWidget from "./components/replacement_pipeline_widget";
import {
  AddressModel,
  getFullAddress,
} from "../../../../service/models/address_model";
import {
  ModalType,
  ModalAlignment,
  useModal,
} from "../../../../context_providers/modal/modal_context";
import AddressPickerWidget from "../../../new_components/common/location/AddressPicker";
import ComplaintRelatedToSearchField from "../create/components/complaints_related_typeable_widget";
import OrderSearchField from "../shared/order_search_field";
import RequiredMark from "../../../new_components/common/required_mark";
import {
  getLockedFieldsFromLookup,
  HomePlatformLookupResult,
  isValidPhoneNumber,
  LockableField,
  mergeComplaintWithLookup,
  normaliseHomePlatformLookup,
  parseDeviceTokens,
  sanitizePhoneNumber,
} from "../shared/home_platform_lookup";
import DeviceTabs, { DeviceVerifyResult } from "../shared/device_tabs";

interface ComplainantDetailPageProps {
  location: any;
}
const todayISTMidnight = () => {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
};

const defaultValue: Complaintmodel = {
  org: "",
  complaint_by: "",
  complainant_name: "",
  complainant_mobile: "",
  reporter_name: "",
  reporter_mobile: "",
  related_to: "",
  subcategory_of_related_to: "",
  category: "",
  subject: "",
  description: "",
  pre_post_delivery: "",
  res_dep: "",
  res_person: "",
  res_person_name: "",
  status: complaintStatus.open,
  ticket_type: ComplaintTicketType.incident,
  source: SourceType.call,
  source_platform: "ERP_Platform",
  state: "",
  pincode: "",
  next_action_date: todayISTMidnight(),
  complaint_date: todayISTMidnight(),
  active: true,
  priority: PriorityType.low,
  createdByDept: "",
  device_working_status: "",
  severity: PriorityType.medium,
  safety_flag: false,
  rca_required: false,
  home_sync_status: "",
  home_sync_failure_reason: "",
};

const SectionCard: FunctionComponent<{
  title: string;
  subtitle?: string;
  children: ReactNode;
}> = ({ title, subtitle, children }) => (
  <div className="border rounded-md bg-white p-3 sm:p-5 space-y-3 sm:space-y-4">
    <div>
      <div className="text-sm font-semibold">{title}</div>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
    <div className="space-y-3 sm:space-y-4">{children}</div>
  </div>
);

const parseApiErrorMessage = (error?: string): string => {
  if (!error) return "";
  try {
    const jsonMatch = error.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed.message) return String(parsed.message);
    }
  } catch {}
  return error;
};

const ComplainantDetailPage: FunctionComponent<ComplainantDetailPageProps> = (
  props,
) => {
  const { showModal, onClose } = useModal();
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();
  const query = new URLSearchParams(props.location.search);
  const id = query.get("id");

  const [complaint, setComplaint] = useState<Complaintmodel>({
    ...defaultValue,
  });
  const [preVal, setPreVal] = useState<Complaintmodel>({
    ...defaultValue,
  });

  const [edit, setEdit] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileLookupLoading, setMobileLookupLoading] = useState(false);
  const [showLogs, setShowLogs] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [disabled, setDisabled] = useState(true);
  const [lookupResult, setLookupResult] = useState<HomePlatformLookupResult>();
  const [selectedLookupDeviceId, setSelectedLookupDeviceId] = useState("");
  const [lockedFields, setLockedFields] = useState<Set<LockableField>>(
    new Set(),
  );
  const [lookupMobile, setLookupMobile] = useState("");
  const [deviceIdVerified, setDeviceIdVerified] = useState<boolean | null>(
    null,
  );
  const [deviceIdVerifyMsg, setDeviceIdVerifyMsg] = useState("");

  // App-created complaints (pre-fix) have no complaint_date — fall back to created_at.
  const ago = getDayAgo(
    new Date((complaint.complaint_date ?? complaint.created_at) as any),
  );

  const isHPCL = complaint.org?.toLowerCase().trim() === "hpcl";
  const isHelpDeskTicket =
    complaint.ticket_type === ComplaintTicketType.help_desk;

  const allowEmptySubCategory = ["Multiverse"];

  const complaintMapping = ComplaintsRelatedToWithSubCategory;

  const complaint_categories = Object.keys(complaintMapping);

  const subCategories = (
    complaint.related_to && complaintMapping[complaint.related_to]
      ? complaintMapping[complaint.related_to]
      : []
  )
    .slice()
    .sort();

  const isSubCategoryRequired: boolean =
    !!complaint.related_to &&
    !allowEmptySubCategory.includes(complaint.related_to);

  const isHomeInstallation = !!complaint.res_dep?.includes("Home Installation");
  // Every Operations sub-department (res_dep "Operations-*") syncs to the Home
  // App, which supports only Incident/Service Request — restrict the ticket
  // type to those two for all of Operations, matching the create form.
  const isOperations = !!complaint.res_dep?.startsWith("Operations");

  const handleComplainantMobileChange = (value: string) => {
    const sanitized = sanitizePhoneNumber(value);
    setComplaint((c) => ({
      ...c,
      complainant_mobile: sanitized,
    }));
  };

  const handleReporterMobileChange = (value: string) => {
    const sanitized = sanitizePhoneNumber(value);
    setComplaint((c) => ({
      ...c,
      reporter_mobile: sanitized,
    }));
  };

  const GetOrgByDevice = async () => {
    setLoading(true);
    const deviceIds = complaint.device_id!.split(",");
    const firstDeviceId = deviceIds[0];
    let res: any;
    if (firstDeviceId) {
      res = await GetOrgByDeviceId(firstDeviceId);
    } else {
      res = await GetOrgByDeviceId(complaint.device_id!);
    }

    if (res.success) {
      if (!res.data) {
        setLoading(false);
        return;
      }

      setComplaint((c) => ({
        ...c,
        org: res.data.org || c.org,
      }));
    } else
      showToast({
        type: ToastType.error,
        text: parseApiErrorMessage(res.error) || "Failed to fetch device info",
      });
    setLoading(false);
  };

  const fetchComplaintContextByMobile = async (mobile?: string) => {
    const phone = sanitizePhoneNumber(
      mobile ?? complaint.complainant_mobile ?? "",
    );

    if (!isValidPhoneNumber(phone)) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid 10 digit mobile number",
      });
      return;
    }

    setMobileLookupLoading(true);
    const res = await GetComplaintCreationDetailsByMobile(phone);

    if (!res.success) {
      setLookupResult(undefined);
      setSelectedLookupDeviceId("");
      setLookupMobile("");
      const rawMsg = parseApiErrorMessage(res.error);
      const isNotFound =
        !rawMsg ||
        rawMsg.toLowerCase().includes("not found") ||
        rawMsg.toLowerCase().includes("installation");
      const parsedMsg = rawMsg || "Unable to fetch complaint details";
      showToast({
        type: isNotFound ? ToastType.warning : ToastType.error,
        text: parsedMsg,
      });
      setMobileLookupLoading(false);
      return;
    }

    const normalisedLookup = normaliseHomePlatformLookup(res.data ?? res);
    const firstDeviceId =
      normalisedLookup.devices.find(
        (device) => device.id === complaint.device_id,
      )?.id ??
      (complaint.device_id?.trim()
        ? complaint.device_id
        : (normalisedLookup.devices[0]?.id ?? ""));

    setLookupResult(normalisedLookup);
    setSelectedLookupDeviceId(firstDeviceId);
    setLookupMobile(phone);
    setLockedFields((prev) => {
      const next = new Set(prev);
      getLockedFieldsFromLookup(normalisedLookup, firstDeviceId).forEach(
        (field) => next.add(field),
      );
      next.add("complainant_mobile");
      return next;
    });
    setComplaint((current) =>
      mergeComplaintWithLookup(
        {
          ...current,
          complainant_mobile: phone,
        },
        normalisedLookup,
        firstDeviceId,
      ),
    );

    if (normalisedLookup.isHomePlatformComplaint) {
      showToast({
        type: ToastType.success,
        text: "Details fetched from Home Platform",
      });
    } else {
      showToast({
        type: ToastType.warning,
        text: "No home platform data found. You can continue editing this complaint manually.",
      });
    }

    setMobileLookupLoading(false);
  };

  const deviceTokens = parseDeviceTokens(complaint.device_id);

  const verifyDeviceForTabs = async (
    deviceId: string,
  ): Promise<DeviceVerifyResult> => {
    const res = await GetComplaintCreationDetailsByMobile(deviceId);
    if (!res?.success) {
      const rawParsed = parseApiErrorMessage(res?.error);
      const reason =
        rawParsed && !rawParsed.toLowerCase().includes("installation")
          ? rawParsed
          : "Device not found on Multiverse";
      return { verified: false, reason };
    }
    const lookup = normaliseHomePlatformLookup(res.data);
    const record =
      lookup.devices.find((d) => d.id.toLowerCase() === deviceId)?.raw ??
      lookup.devices[0]?.raw ??
      lookup.raw?.[0];
    const found =
      lookup.devices.length > 0 ||
      (res.data && Object.keys(res.data).length > 0);
    if (!found) {
      return {
        verified: false,
        reason:
          "Device ID not found in Multiverse. Please check and try again.",
      };
    }
    return {
      verified: true,
      details: {
        product_name: record?.product_name ?? record?.productName,
        firmware_version: record?.firmware_version ?? record?.firmwareVersion,
        hardware_version: record?.hardware_version ?? record?.hardwareVersion,
        batch_number: record?.batch_number ?? record?.batchNumber,
        mfg_date: record?.mfg_date ?? record?.mfgDate,
        installation_date:
          record?.installationDate ??
          record?.installation_date ??
          record?.createdAt,
      },
    };
  };

  const handleDeviceTabsChange = (next: {
    devices: string[];
    deviceMeta: typeof complaint.device_meta;
  }) => {
    setComplaint((c) => ({
      ...c,
      device_id: next.devices.length ? next.devices.join(",") : "",
      device_meta: next.devices.length ? next.deviceMeta : undefined,
    }));
  };

  useEffect(() => {
    if (complaint.device_id && !edit) {
      GetOrgByDevice();
    }
  }, [complaint.device_id, edit]);

  useEffect(() => {
    if (!lookupResult || !selectedLookupDeviceId) return;

    setLockedFields((prev) => {
      const next = new Set(prev);
      getLockedFieldsFromLookup(lookupResult, selectedLookupDeviceId).forEach(
        (field) => next.add(field),
      );
      return next;
    });
    setComplaint((current) =>
      mergeComplaintWithLookup(current, lookupResult, selectedLookupDeviceId),
    );
  }, [lookupResult, selectedLookupDeviceId]);

  useEffect(() => {
    if (lookupResult && complaint.complainant_mobile !== lookupMobile) {
      setLookupResult(undefined);
      setSelectedLookupDeviceId("");
      setLookupMobile("");
      setLockedFields(new Set());
    }
  }, [complaint.complainant_mobile, lookupMobile, lookupResult]);

  const getComplaint = async () => {
    if (!id) return;
    setLoading(true);
    const res = await GetComplaint(id);

    if (res.success) {
      if (!res.data.address && res.data.city) {
        res.data.address = {
          line1: "",
          city: res.data.city,
          state: res.data.state,
          pincode: res.data.pincode,
          full_address: getFullAddress({
            line1: "",
            city: res.data.city,
            state: res.data.state,
            country: "India",
            pincode: res.data.pincode,
          }),
        };
      }
      const data = {
        ...res.data,
        source_platform: res.data.source_platform ?? "ERP_Platform",
      };

      const legacyTokens = parseDeviceTokens(data.device_id);
      if (legacyTokens.length && !data.device_meta) {
        const seeded: NonNullable<typeof data.device_meta> = {};
        legacyTokens.forEach((tok, idx) => {
          seeded[tok] = {
            working_status: data.device_working_status || undefined,
            verified: true,
            ...(idx === 0 && data.oem_service_ticket_id
              ? { oem_service_ticket_id: data.oem_service_ticket_id }
              : {}),
          };
        });
        data.device_meta = seeded;
      }

      setComplaint(data);
      setPreVal(data);
    }

    setLoading(false);
  };

  const checkValidationsForHelpDeskTickets = () => {
    if (
      complaint.ticket_type &&
      complaint.ticket_type.toLowerCase() === "help desk"
    ) {
      if (
        !complaint.complainant_name ||
        complaint.complainant_name.trim() === ""
      ) {
        showToast({
          text: "Please enter complainant name",
          type: ToastType.error,
        });
        return true;
      }
      if (
        !complaint.complainant_mobile ||
        complaint.complainant_mobile.trim() === ""
      ) {
        showToast({
          text: "Please enter complainant contact number",
          type: ToastType.error,
        });
        return true;
      }
      if (
        complaint.complaint_by &&
        complaint_by.indexOf(complaint.complaint_by) === -1
      ) {
        showToast({
          text: `Please select valid complaint by`,
          type: ToastType.error,
        });
        return true;
      }
      if (!complaint.org || complaint.org.trim() === "") {
        showToast({
          text: "Please select organization",
          type: ToastType.error,
        });
        return true;
      }
      if (!complaint.related_to || complaint.related_to.trim() === "") {
        showToast({
          text: "Please select complaint related to",
          type: ToastType.error,
        });
        return true;
      }
      if (
        complaint.source &&
        Object.values(SourceType).indexOf(complaint.source as any) === -1
      ) {
        showToast({
          text: `Please select valid source`,
          type: ToastType.error,
        });
        return true;
      }
    }
    return false;
  };

  const submit = async (e: any) => {
    e.preventDefault();

    let isError = checkValidationsForHelpDeskTickets();
    if (isError) return;

    const phone = sanitizePhoneNumber(complaint.complainant_mobile ?? "");
    if (!isValidPhoneNumber(phone)) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid complainant contact number",
      });
      return;
    }

    if (lookupResult?.devices.length && !selectedLookupDeviceId) {
      showToast({
        type: ToastType.error,
        text: "Please select a device linked to the entered mobile number",
      });
      return;
    }

    if (!complaint.subject?.trim()) {
      showToast({
        type: ToastType.error,
        text: "Please enter subject",
      });
      return;
    }

    // if (!complaint.description?.trim()) {
    //   showToast({
    //     type: ToastType.error,
    //     text: "Please enter description",
    //   });
    // }

    let ids = complaint.device_id ? complaint.device_id.split(",") : [];
    // UBC devices use ids like "<id>_1" for sub-devices; skip the standard
    // 6-char device id regex for those complaints.
    const skipDeviceIdValidation = complaint.related_to === "UBC";
    const validator = DeviceIdRegex;
    for (let i = 0; i < ids.length; i++) {
      let id = ids[i].trim().toLowerCase();

      if (!skipDeviceIdValidation && !validator.test(id)) {
        showToast({
          type: ToastType.warning,
          text: `device id : ${id} is not valid`,
        });
        return;
      }
      ids[i] = id;
    }

    complaint.device_id = ids.length ? ids.join(",") : "";
    complaint.complainant_mobile = phone;

    // Mirror the first device's status into the legacy flat field for the
    // backend's required device_working_status + existing exports/reports.
    if (ids.length) {
      const firstStatus = complaint.device_meta?.[ids[0]]?.working_status;
      complaint.device_working_status =
        firstStatus || complaint.device_working_status || "Not Working";

      // Keep device_meta keys ⊆ device_id (backend refine rejects stray keys).
      if (complaint.device_meta) {
        const pruned: NonNullable<typeof complaint.device_meta> = {};
        ids.forEach((d) => {
          if (complaint.device_meta?.[d]) pruned[d] = complaint.device_meta[d];
        });
        complaint.device_meta = pruned;
      }
    } else {
      complaint.device_meta = undefined;
    }

    if (
      complaint.complaint_by?.toLowerCase() === "host" &&
      !complaint.order_id &&
      complaint.org &&
      complaint.org.toLowerCase() !== "hpcl" &&
      complaint.ticket_type !== ComplaintTicketType.help_desk &&
      complaint.created_by !== "Kazam_App" &&
      !isHomeInstallation
    ) {
      return showToast({
        text: "Please select order id",
        type: ToastType.error,
      });
    }

    // Remarks non-mandatory — removed validation for Solved/Resolved status
    // if (!complaint.remarks && complaint.status === "Solved") {
    //   return showToast({
    //     text: "Please enter the detailed remarks.",
    //     type: ToastType.error,
    //   });
    // }

    setLoading(true);
    let form = new FormData();

    form.append("data", JSON.stringify(complaint));

    const res = await UpdateComplaint({ ...complaint, id: id! });

    if (res.success) {
      setEdit(false);
      setDeviceIdVerified(null);
      setDeviceIdVerifyMsg("");
      showToast({
        type: ToastType.success,
        text: `Complaint Updated successfully complaint ID: ${res.data.id}`,
      });
      if (res.homeAppInfoUpdated === true) {
        showToast({
          type: ToastType.success,
          text: "Information updated on Home App",
        });
      }
      if (res.homeAppTicketCreated === true) {
        showToast({
          type: ToastType.success,
          text: "Complaint ticket created on Home App",
        });
      } else if (res.homeAppTicketCreated === false) {
        showToast({
          type: ToastType.error,
          text: "Failed to create complaint ticket on Home App",
        });
      }
      if (complaint.related_to === "UBC") {
        console.log("UBC complaint detected, sending webhook...");
        try {
          const webhookPayload = {
            id: complaint.id,
            status: complaint.status,
            remarks: complaint.remarks,
          };
          console.log("Webhook payload:", webhookPayload);
          const webhookRes = await fetch(
            "https://backend.ubc.kazam.energy/erp-support-webhook",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(webhookPayload),
            },
          );
          console.log(webhookRes);
          console.log("Webhook response status:", webhookRes.status);
          const webhookData = await webhookRes.text();
          console.log("Webhook response data:", webhookData);
        } catch (err) {
          console.error("UBC webhook call failed:", err);
        }
      } else {
        console.log(
          "Skipping UBC webhook, related_to is:",
          complaint.related_to,
        );
      }
      getComplaint();
    } else {
      showToast({
        type: ToastType.error,
        text: parseApiErrorMessage(res.error) || "Failed to update complaint",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    setDisabled(
      !isAuthorised({ module: MODULE.complaint, action: [ACTION.UPDATE] }),
    );
  }, []);

  useEffect(() => {
    getComplaint();
  }, []);

  useEffect(() => {
    if (
      complaint.id &&
      isValidPhoneNumber(complaint.complainant_mobile) &&
      !lookupResult &&
      !mobileLookupLoading
    ) {
      fetchComplaintContextByMobile(complaint.complainant_mobile);
    }
  }, [complaint.id]);

  useEffect(() => {
    if (complaint.complaint_by === "Dealer") {
      setComplaint((c) => ({ ...c, complaint_by: "Dealers host" }));
    }
  }, [complaint.id]);

  useEffect(() => {
    if (isHomeInstallation && !complaint.complaint_by) {
      setComplaint((c) => ({ ...c, complaint_by: "Host" }));
    }
  }, [isHomeInstallation]);

  useEffect(() => {
    if (!isHomeInstallation || !complaint.ticket_type) return;
    if (
      complaint.ticket_type === "service" ||
      complaint.ticket_type === "incident"
    )
      return;
    const normalised =
      complaint.ticket_type === "Service Request" ? "service" : "incident";
    setComplaint((c) => ({ ...c, ticket_type: normalised }));
  }, [complaint.id, isHomeInstallation]);

  // Normalize HP symptom values to ERP category/subcategory using the same mapping as create page
  useEffect(() => {
    if (!isHomeInstallation || !complaint.related_to) return;
    // If related_to is already a valid ERP category key, nothing to do
    if (complaintMapping[complaint.related_to]) return;
    // It's an HP symptom value — find its label from DEALER_SERVICE_RELATED_TO_OPTIONS
    const hpOption = DEALER_SERVICE_RELATED_TO_OPTIONS.find(
      (o) => o.value === complaint.related_to,
    );
    if (!hpOption) return;
    // Find which ERP category contains this label as a subcategory
    const erpCategory = Object.keys(complaintMapping).find((cat) =>
      complaintMapping[cat].includes(hpOption.label),
    );
    if (erpCategory) {
      setComplaint((c) => ({
        ...c,
        related_to: erpCategory,
        subcategory_of_related_to:
          c.subcategory_of_related_to || hpOption.label,
      }));
    }
  }, [complaint.id, isHomeInstallation]);

  useEffect(() => {
    if (
      complaint.related_to &&
      !complaint_categories.includes(complaint.related_to)
    ) {
      for (const [category, subcategories] of Object.entries(
        ComplaintsRelatedToWithSubCategory,
      )) {
        if ((subcategories as string[]).includes(complaint.related_to)) {
          setComplaint((c) => ({
            ...c,
            related_to: category,
            subcategory_of_related_to:
              c.subcategory_of_related_to || c.related_to,
          }));
          break;
        }
      }
    }
  }, [complaint.related_to]);

  return (
    <div className="p-3 sm:p-5 mb-5 text-sm">
      {complaint.id && showComments && (
        <DrawerModal
          id="complaint_detail_comment_drawer"
          show={showComments}
          title={"Comments"}
          location={DrawerModalLoaction.right}
          onClose={() => {
            setShowComments(false);
          }}
        >
          <CommentWidget complaint_id={complaint!.id} />
        </DrawerModal>
      )}
      {complaint.id && showLogs && (
        <DrawerModal
          id="complaint_detail_history_drawer"
          show={showLogs}
          title={"Log history"}
          location={DrawerModalLoaction.right}
          onClose={() => {
            setShowLogs(false);
          }}
        >
          <HistoryWidget complaint_id={complaint!.id} />
        </DrawerModal>
      )}
      {complaint.id && showAttachments && (
        <ModalDialog
          id="complaint_detail_attachments_modal"
          show={showAttachments}
          title="Attachments"
          onClose={() => setShowAttachments(false)}
        >
          <AttachmentWidget complaint_id={complaint.id} />
        </ModalDialog>
      )}

      <div className="flex justify-between my-1 flex-wrap">
        <div className="">
          <div className="text-gray-500 font-semibold">Complained</div>
          {ago === 0 ? (
            <div className="font-bold text-2xl sm:text-4xl">Today</div>
          ) : (
            <div className="flex items-end gap-2">
              <div className="text-3xl sm:text-6xl font-bold">{ago}</div>
              <div className="text-base sm:text-xl">Days Ago</div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 items-end flex-wrap justify-between sm:justify-start">
          <h1 className="text-base sm:text-lg font-semibold">
            <span className="text-sm text-gray-500 font-semibold">
              Complaint ID
            </span>{" "}
            &middot; {complaint.id}
          </h1>
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs ${
                complaint.active
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {complaint.active ? "Active" : "Closed"}
            </span>
            {!disabled && (
              <button
                id="complaint_detail_edit_btn"
                type="button"
                onClick={() => setEdit(true)}
                className={
                  " rounded border px-3 py-1 hover:bg-black hover:text-white bg-white " +
                  (edit ? "hidden" : "block")
                }
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      <form
        onSubmit={submit}
        autoComplete="new-password"
        className={`space-y-6 ${edit ? "pb-24" : ""}`}
      >
        <SectionCard
          title="Device identification"
          subtitle="Customer number lookup"
        >
          <div className="flex flex-row gap-2 items-stretch">
            <div className="flex flex-col flex-1">
              <label
                htmlFor="complainant_number_lookup"
                className="mr-2 text-sm leading-7"
              >
                Registered Mobile Number <RequiredMark />
              </label>
              <input
                disabled={!edit}
                required
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                name="complainant_number_lookup"
                id="complainant_number_lookup"
                placeholder="Enter 10 digit mobile number"
                className="border px-2 py-2 h-full"
                value={complaint.complainant_mobile ?? ""}
                onChange={(e) => handleComplainantMobileChange(e.target.value)}
                onBlur={() => {
                  if (
                    edit &&
                    isValidPhoneNumber(complaint.complainant_mobile)
                  ) {
                    fetchComplaintContextByMobile();
                  }
                }}
              />
            </div>
          </div>

          <div className="flex flex-col mt-3">
            <label className="text-sm leading-7 text-gray-600 font-medium">
              Device ids
            </label>
            {edit ? (
              // Edit mode: remove existing and re-fetch/verify to add (tabs).
              <DeviceTabs
                devices={deviceTokens}
                deviceMeta={complaint.device_meta ?? {}}
                editable={true}
                linkedDevices={lookupResult?.devices ?? []}
                verifyDevice={verifyDeviceForTabs}
                onChange={handleDeviceTabsChange}
                skipDeviceIdValidation={complaint.related_to === "UBC"}
              />
            ) : deviceTokens.length ? (
              // View mode: just show all device ids in one read-only field.
              <div className="flex flex-wrap gap-2">
                {deviceTokens.map((id) => (
                  <span
                    key={id}
                    className="rounded-full border px-3 py-1 text-sm bg-gray-50 text-gray-700"
                  >
                    {id}
                  </span>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-amber-300 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                No device ID linked to this complaint. Click{" "}
                <strong>Edit</strong> to add one.
              </div>
            )}
          </div>

          {(complaint.complaint_by ?? "").toLowerCase() === "host" &&
            (edit ? (
              <div className="flex flex-col max-w-sm mt-3">
                <label
                  htmlFor="complaint_order_id"
                  className="mr-2 text-sm leading-7"
                >
                  Order ID
                </label>
                <OrderSearchField
                  id="complaint_order_id"
                  value={complaint.order_id ?? ""}
                  onSelect={(order_id) =>
                    setComplaint((c) => ({ ...c, order_id }))
                  }
                />
              </div>
            ) : (
              complaint.order_id && (
                <div className="text-sm mt-3">
                  <NavLink
                    id="complaint_detail_goto_order_link"
                    target="_blank"
                    to={"/orders/" + complaint.order_id}
                  >
                    <div className="mx-1 cursor-pointer flex text-blue-400">
                      Goto : {complaint.order_id}
                    </div>
                  </NavLink>
                </div>
              )
            ))}

          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span
              className={`rounded-full px-3 py-1 ${
                lookupResult?.isHomePlatformComplaint
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {lookupResult?.isHomePlatformComplaint
                ? "Home platform complaint"
                : "Manual / ERP complaint"}
            </span>
            {lookupResult?.orgName && (
              <span className="rounded-full px-3 py-1 bg-blue-50 text-blue-700">
                Org: {lookupResult.orgName}
              </span>
            )}
            {!!lookupResult?.complaints.length && (
              <span className="rounded-full px-3 py-1 bg-amber-50 text-amber-700">
                Previous complaints: {lookupResult?.complaints.length}
              </span>
            )}
          </div>

          {lookupResult?.devices.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <div className="flex flex-col">
                <label className="text-sm leading-7">
                  Device ID <RequiredMark />
                </label>
                <select
                  id="complaint_detail_lookup_device_select"
                  disabled={!edit}
                  value={selectedLookupDeviceId}
                  onChange={(e) => setSelectedLookupDeviceId(e.target.value)}
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select device
                  </option>
                  {lookupResult.devices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="border rounded-md p-3 bg-gray-50 text-sm">
                <div className="font-medium text-gray-700 mb-2">
                  Auto-filled details
                </div>
                <div className="space-y-1 text-gray-600">
                  <div>Customer: {lookupResult.customerName || "--"}</div>
                  <div>Address: {lookupResult.customerAddress || "--"}</div>
                  <div>
                    City / State:{" "}
                    {[lookupResult.customerCity, lookupResult.customerState]
                      .filter(Boolean)
                      .join(", ") || "--"}
                  </div>
                  <div>Pincode: {lookupResult.customerPincode || "--"}</div>
                </div>
              </div>
            </div>
          ) : lookupResult ? (
            <div className="mt-4 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-600">
              No linked devices were returned for this mobile number. You can
              continue updating the complaint manually.
            </div>
          ) : null}

          {!!lookupResult?.complaints.length && (
            <div className="mt-4">
              <div className="text-sm font-medium mb-2">
                Existing complaints
              </div>
              <div className="space-y-2">
                {lookupResult.complaints.slice(0, 3).map((item) => (
                  <div
                    id={`complaint_detail_lookup_prev_complaint_${item.id}`}
                    key={item.id}
                    className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm"
                  >
                    <div className="font-medium text-gray-700">{item.id}</div>
                    <div className="text-gray-600">{item.subject || "--"}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Status: {item.status || "--"}
                      {item.deviceId ? ` | Device: ${item.deviceId}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        <div className="space-y-6">
          <SectionCard
            title="Reporter / complainant"
            subtitle="Reporter name, phone, and role"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div className="flex flex-col sm:col-span-2 lg:col-span-4">
                <label className="text-sm">Organization Name</label>
                <input
                  type="text"
                  disabled
                  readOnly
                  value={complaint.org ?? ""}
                  placeholder="Org name"
                  className="border px-2 py-1 bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="reporter_name" className="mr-2 text-sm">
                  Reporter name <RequiredMark />
                </label>
                <input
                  disabled={!edit}
                  required
                  type="text"
                  name="reporter_name"
                  id="reporter_name"
                  placeholder="Name of reporter"
                  className="border px-2 py-1"
                  value={
                    (complaint.reporter_name || complaint.complaint_by) ?? ""
                  }
                  onChange={(e) =>
                    setComplaint((c) => ({
                      ...c,
                      reporter_name: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="reporter_phone" className="mr-2 text-sm">
                  Reporter phone <RequiredMark />
                </label>
                <input
                  disabled={!edit}
                  required
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  name="reporter_phone"
                  id="reporter_phone"
                  placeholder="Contact number"
                  className="border px-2 py-1"
                  value={
                    (complaint.reporter_mobile ||
                      complaint.complainant_mobile) ??
                    ""
                  }
                  onChange={(e) => handleReporterMobileChange(e.target.value)}
                  onBlur={() => {
                    if (
                      edit &&
                      isValidPhoneNumber(complaint.reporter_mobile) &&
                      !lookupResult
                    ) {
                      fetchComplaintContextByMobile(complaint.reporter_mobile);
                    }
                  }}
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="complaint_by" className="mr-2 text-sm">
                  Reporter role <RequiredMark />
                </label>
                <select
                  disabled={!edit}
                  required
                  value={complaint.complaint_by}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      complaint_by: e.target.value,
                    }));
                  }}
                  name="complaint_by"
                  id="complaint_by"
                  className="border text-sm px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {complaint_by.map((d, i) => (
                    <option key={i} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                {complaint.complaint_by &&
                  complaint_by.indexOf(complaint.complaint_by) === -1 && (
                    <p className="text-xs text-red-400">
                      Please reselect prev val {complaint.complaint_by}
                    </p>
                  )}
              </div>

              {!isHPCL && (
                <div className="flex flex-col">
                  <label className="mr-2 text-sm leading-7">
                    Pre/Post Delivery {!isHelpDeskTicket && <RequiredMark />}
                  </label>

                  <div className="border p-2 flex items-center gap-6">
                    {/* PRE */}
                    <label
                      htmlFor="complaint_detail_pre_delivery_radio"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        id="complaint_detail_pre_delivery_radio"
                        name="pre_post_delivery" // ✅ SAME GROUP NAME
                        value="Pre Delivery" // ✅ CONSISTENT VALUE
                        disabled={!edit}
                        checked={complaint.pre_post_delivery === "Pre Delivery"}
                        onChange={(e) =>
                          setComplaint((c) => ({
                            ...c,
                            pre_post_delivery: e.target.value,
                          }))
                        }
                        required={!isHelpDeskTicket}
                      />
                      Pre
                    </label>

                    {/* POST */}
                    <label
                      htmlFor="complaint_detail_post_delivery_radio"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        id="complaint_detail_post_delivery_radio"
                        name="pre_post_delivery" // ✅ SAME GROUP NAME
                        value="Post Delivery"
                        disabled={!edit}
                        checked={
                          complaint.pre_post_delivery === "Post Delivery"
                        }
                        onChange={(e) =>
                          setComplaint((c) => ({
                            ...c,
                            pre_post_delivery: e.target.value,
                          }))
                        }
                      />
                      Post
                    </label>
                  </div>
                </div>
              )}

              <div className="flex flex-col">
                <label htmlFor="res_dep" className="mr-2 text-sm leading-7">
                  Reponsible department {!isHelpDeskTicket && <RequiredMark />}
                </label>
                <select
                  disabled={!edit}
                  required={!isHelpDeskTicket}
                  value={complaint.res_dep}
                  onChange={(e) => {
                    const newDep = e.target.value;
                    setComplaint((c) => ({
                      ...c,
                      res_dep: newDep,
                      ...(newDep.includes("Home Installation") &&
                      !["incident", "service"].includes(c.ticket_type ?? "")
                        ? { ticket_type: "incident" }
                        : {}),
                    }));
                  }}
                  name="res_dep"
                  id="res_dep"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {Object.keys(SubDepartments).map((k, i1) => (
                    <optgroup label={k} key={i1}>
                      {SubDepartments[k].map((sub_dep, i2) => (
                        <option
                          value={`${k}-${sub_dep}`}
                          key={i2}
                        >{`${sub_dep}`}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="res_person" className="mr-2 text-sm leading-7">
                  Reponsible person (Primary){" "}
                  {!isHelpDeskTicket && <RequiredMark />}
                </label>
                {!loading && (
                  <EmployeeSearchWidget
                    id="complaint_detail_res_person_search"
                    withBtn={false}
                    onSelect={(em) => {
                      const department = em?.emp_profile?.department;
                      const subDep = em?.emp_profile?.sub_dep;
                      setComplaint((c) => ({
                        ...c,
                        res_person: em.uid!,
                        res_person_name: em.first_name + " " + em.last_name,
                        // don't overwrite res_dep for Home Installation
                        ...(!c.res_dep?.includes("Home Installation") &&
                        department &&
                        subDep
                          ? { res_dep: `${department}-${subDep}` }
                          : {}),
                      }));
                    }}
                    onClear={() => {
                      setComplaint((c) => ({
                        ...c,
                        res_person: "",
                        res_person_name: "",
                        ...(!c.res_dep?.includes("Home Installation")
                          ? { res_dep: "" }
                          : {}),
                      }));
                    }}
                    emp={
                      complaint.res_person
                        ? {
                            uid: complaint.res_person,
                            name: complaint.res_person_name!,
                          }
                        : undefined
                    }
                    disabled={!edit}
                    required={!isHelpDeskTicket}
                    placeholder="Primary person"
                    sub_dep={
                      isHomeInstallation
                        ? undefined
                        : complaint.res_dep?.split("-")[1]
                    }
                    department={isHomeInstallation ? "Operations" : undefined}
                  />
                )}
              </div>

              <div className="flex flex-col">
                <label
                  htmlFor="res_person_sec_dept"
                  className="mr-2 text-sm leading-7"
                >
                  Secondary person department
                </label>
                <select
                  disabled={!edit}
                  value={complaint.res_person_sec_dept || ""}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      res_person_sec_dept: e.target.value,
                      // Clear secondary person when department changes
                      res_person_sec_id: "",
                      res_person_sec_name: "",
                    }));
                  }}
                  name="res_person_sec_dept"
                  id="res_person_sec_dept"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {Object.keys(SubDepartments).map((k, i1) => (
                    <optgroup label={k} key={i1}>
                      {SubDepartments[k].map((sub_dep, i2) => (
                        <option
                          value={`${k}-${sub_dep}`}
                          key={i2}
                        >{`${sub_dep}`}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label htmlFor="res_person" className="mr-2 text-sm leading-7">
                  Reponsible person (Secondary)
                </label>
                {!loading && (
                  <EmployeeSearchWidget
                    id="complaint_detail_res_person_sec_search"
                    withBtn={false}
                    onSelect={(em) => {
                      const department = em?.emp_profile?.department;
                      const subDep = em?.emp_profile?.sub_dep;

                      setComplaint((c) => ({
                        ...c,
                        res_person_sec_id: em.uid!,
                        res_person_sec_name: em.first_name + " " + em.last_name,
                        res_person_sec_dept:
                          department && subDep ? `${department}-${subDep}` : "",
                      }));
                    }}
                    onClear={() => {
                      setComplaint((c) => ({
                        ...c,
                        res_person_sec_id: "",
                        res_person_sec_name: "",
                        res_person_sec_dept: "",
                      }));
                    }}
                    emp={
                      complaint.res_person_sec_id
                        ? {
                            uid: complaint.res_person_sec_id,
                            name: complaint.res_person_sec_name ?? "",
                          }
                        : undefined
                    }
                    disabled={!edit}
                    placeholder="Secondary person"
                    sub_dep={complaint.res_person_sec_dept?.split("-")[1]}
                  />
                )}
              </div>

              {!isHPCL && (
                <div className="flex flex-col">
                  <label htmlFor="category" className="mr-2 text-sm leading-7">
                    Category {!isHelpDeskTicket && <RequiredMark />}
                  </label>
                  <select
                    required={!isHelpDeskTicket}
                    disabled={!edit}
                    value={complaint.category}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        category: e.target.value,
                      }));
                    }}
                    name="category"
                    id="category"
                    className="border px-2 py-1"
                  >
                    <option value="" disabled hidden>
                      Select
                    </option>
                    {[
                      "Fleets/HUB",
                      "Home Installation",
                      "Individual",
                      "RWA",
                      "Others",
                    ].map((c, i) => (
                      <option value={c} key={i}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col">
                <label htmlFor="source" className="mr-2 text-sm leading-7">
                  Source <RequiredMark />
                </label>
                <select
                  disabled={!edit}
                  value={complaint.source || ""}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      source: e.target.value,
                    }));
                  }}
                  name="source"
                  id="source"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {Object.values(SourceType).map((c, i) => (
                    <option value={c} key={i}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="mr-2 text-sm leading-7">
                  Source Platform
                </label>
                <input
                  disabled
                  value={complaint.source_platform ?? "--"}
                  className="border px-2 py-1 bg-gray-50 text-gray-500"
                />
              </div>
              {complaint.oem_service_ticket_id || complaint.ticketId ? (
                <>
                  <div className="flex flex-col">
                    <label className="mr-2 text-sm leading-7">
                      Service Ticket ID
                    </label>
                    <a
                      id="complaint_detail_service_ticket_link"
                      href={`https://home.kazam.in/service-tickets/${
                        complaint.oem_service_ticket_id || complaint.ticketId
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border px-2 py-1 bg-gray-50 text-blue-600 hover:underline hover:bg-gray-100"
                    >
                      {complaint.oem_service_ticket_id || complaint.ticketId}
                    </a>
                  </div>
                  {complaint.home_installation_id && (
                    <div className="flex flex-col">
                      <label className="mr-2 text-sm leading-7">
                        Installation ID
                      </label>
                      <input
                        disabled
                        value={complaint.home_installation_id}
                        className="border px-2 py-1 bg-gray-50 text-gray-500"
                      />
                    </div>
                  )}
                </>
              ) : isHomeInstallation ? (
                <div className="flex flex-col">
                  <label className="mr-2 text-sm leading-7">
                    Service Ticket ID
                  </label>
                  <p className="text-sm text-red-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    {complaint.home_sync_failure_reason
                      ?.toLowerCase()
                      .includes("closed installation not found")
                      ? "No WCR / Installation is found for this device id in Home App. Complaint was not created on Home App."
                      : complaint.home_sync_failure_reason ||
                        "No WCR / Installation is found for this device id in Home App. Complaint was not created on Home App."}
                  </p>
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard title="Customer" subtitle="Host details and address">
            <div className="bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
                <div className="flex flex-col">
                  <label
                    htmlFor="complainant_name"
                    className="mr-2 text-sm leading-7"
                  >
                    Complainant Name {!isHPCL && <RequiredMark />}
                  </label>
                  <input
                    disabled={!edit || lockedFields.has("complainant_name")}
                    required={isHPCL || !isHelpDeskTicket ? false : true}
                    value={complaint.complainant_name}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        complainant_name: e.target.value,
                      }));
                    }}
                    className="border px-2 py-1"
                    type="text"
                    name="complainant_name"
                    id="complainant_name"
                    placeholder="name"
                  />
                </div>

                <div className="flex flex-col">
                  <label
                    htmlFor="complainant_number"
                    className="mr-2 text-sm leading-7"
                  >
                    Complainant Contact <RequiredMark />
                  </label>
                  <input
                    disabled={!edit || lockedFields.has("complainant_mobile")}
                    required={isHelpDeskTicket}
                    value={complaint.complainant_mobile}
                    onChange={(e) => {
                      const value = sanitizePhoneNumber(e.target.value);
                      setComplaint((c) => ({
                        ...c,
                        complainant_mobile: value,
                      }));
                    }}
                    onBlur={() => {
                      if (
                        edit &&
                        isValidPhoneNumber(complaint.complainant_mobile) &&
                        !lookupResult
                      ) {
                        fetchComplaintContextByMobile();
                      }
                    }}
                    className="border px-2 py-1"
                    type="tel"
                    inputMode="numeric"
                    title="Please enter a valid 10 digit mobile number"
                    name="complainant_mobile"
                    id="complainant_number"
                    placeholder="Contact number"
                  />
                </div>

                {!isHPCL && (
                  <div className="flex flex-col">
                    <label
                      htmlFor="alt_number"
                      className="mr-2 text-sm leading-7"
                    >
                      Alternative Contact
                    </label>
                    <input
                      disabled={!edit}
                      value={complaint.alt_mobile}
                      onChange={(e) => {
                        setComplaint((c) => ({
                          ...c,
                          alt_mobile: sanitizePhoneNumber(e.target.value),
                        }));
                      }}
                      className="border px-2 py-1"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      // pattern="[1-9]{1}[0-9]{9}"
                      title="Please enter a valid 10 digit mobile number"
                      name="alt_mobile"
                      id="alt_number"
                      placeholder="Alternative contact number"
                    />
                  </div>
                )}

                {!isHPCL && (
                  <div className="flex flex-col">
                    <label
                      htmlFor="dealer_id"
                      className="mr-2 text-sm leading-7"
                    >
                      Dealer ID
                    </label>
                    <input
                      disabled={!edit || lockedFields.has("dealer_id")}
                      value={complaint.dealer_id}
                      onChange={(e) => {
                        setComplaint((c) => ({
                          ...c,
                          dealer_id: e.target.value,
                        }));
                      }}
                      className="border px-2 py-1"
                      type="text"
                      name="dealer_id"
                      id="dealer_id"
                      placeholder="Dealer ID"
                    />
                  </div>
                )}

                {!isHPCL && (
                  <div className="flex flex-col col-span-2">
                    <label htmlFor="address-picker">Address</label>
                    <div
                      id="complaint_detail_address_picker"
                      onClick={() => {
                        if (!edit || lockedFields.has("address")) return;
                        showModal({
                          type: ModalType.drawer,
                          title: "Address Picker",
                          alignment: ModalAlignment.right,
                          container(id) {
                            return (
                              <div className="bg-gray-100 p-1 h-full w-full">
                                <AddressPickerWidget
                                  data={complaint.address}
                                  location_required={false}
                                  onSubmit={function (
                                    _address: AddressModel,
                                  ): void {
                                    setComplaint((o) => ({
                                      ...o!,
                                      address: { ...o!.address, ..._address },
                                    }));
                                    onClose(id);
                                  }}
                                  onCancel={function (): void {
                                    onClose(id);
                                  }}
                                />
                              </div>
                            );
                          },
                        });
                      }}
                      className={`border rounded px-2 p-1 focus:outline-none h-32 ${
                        edit && !lockedFields.has("address")
                          ? ""
                          : "opacity-60 cursor-not-allowed"
                      }`}
                    >
                      {(complaint.address && complaint.address.full_address) ??
                        "Full address"}
                    </div>
                  </div>
                )}

                {!isHPCL && (
                  <div className="flex flex-col">
                    <label htmlFor="state" className="mr-2 text-sm leading-7">
                      State {!isHelpDeskTicket && <RequiredMark />}
                    </label>
                    <input
                      disabled={!edit || lockedFields.has("state")}
                      value={complaint.state}
                      onChange={(e) => {
                        setComplaint((c) => ({
                          ...c,
                          state: e.target.value,
                        }));
                      }}
                      className="border px-2 py-1"
                      type="text"
                      name="state"
                      id="state"
                      placeholder="State"
                      required={!isHelpDeskTicket}
                    />
                  </div>
                )}

                {!isHPCL && (
                  <div className="flex flex-col w-full">
                    <label htmlFor="pincode" className="mr-2 text-sm leading-7">
                      Postal code
                    </label>
                    <input
                      disabled={!edit || lockedFields.has("pincode")}
                      type="number"
                      name="pincode"
                      placeholder="Postal code"
                      value={complaint.pincode}
                      onChange={(e) => {
                        const t = e.target.value;
                        if (e.target.value.length <= 6)
                          setComplaint((o) => ({ ...o!, pincode: t }));
                      }}
                      className="border px-2 py-1"
                      id="pincode"
                    />
                  </div>
                )}

                {complaint.incident_id && (
                  <div className="flex flex-col w-full">
                    <label
                      htmlFor="incident_id"
                      className="mr-2 text-sm leading-7"
                    >
                      Service Desk ID <RequiredMark />
                    </label>
                    <input
                      disabled
                      type="text"
                      name="incident_id"
                      placeholder="Service Desk ID"
                      value={complaint.incident_id}
                      className="border px-2 py-1"
                      id="incident_id"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Host + Dealer details section — commented out (data not populated during creation)
              <div className="mt-4 border rounded-md p-4 bg-gray-50 space-y-4">
                ...
              </div>
              */}
            </div>
          </SectionCard>
        </div>

        <>
          <SectionCard
            title="Problem details"
            subtitle="Issue category, subject, and description"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
              <div className="flex flex-col">
                <label htmlFor="ticket_type" className="mr-2 text-sm leading-7">
                  Ticket Type {!isHelpDeskTicket && <RequiredMark />}
                </label>
                <select
                  required
                  disabled={!edit}
                  value={complaint.ticket_type}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      ticket_type: e.target.value,
                    }));
                  }}
                  name="ticket_type"
                  id="ticket_type"
                  className="border px-2 py-1"
                >
                  <option value="" disabled hidden>
                    Select
                  </option>
                  {isOperations ? (
                    <>
                      <option value={HomeInstallTicketType.incident}>
                        Incident
                      </option>
                      <option value={HomeInstallTicketType.service}>
                        Service Request
                      </option>
                    </>
                  ) : (
                    Object.values(ComplaintTicketType)
                      .sort()
                      .map((c, i) => (
                        <option value={c} key={i}>
                          {c}
                        </option>
                      ))
                  )}
                </select>
              </div>

              {!isHomeInstallation && (
                <div className="flex flex-col">
                  <ComplaintRelatedToSearchField
                    label="Symptom (as reported)"
                    value={complaint.related_to}
                    options={complaint_categories}
                    onChange={(value) => {
                      setComplaint((c: any) => ({
                        ...c,
                        related_to: value,
                        subcategory_of_related_to: "",
                      }));
                    }}
                    onClear={() => {
                      setComplaint((c: any) => ({
                        ...c,
                        related_to: "",
                        subcategory_of_related_to: "",
                      }));
                    }}
                    id="complaint_related_to"
                    name="related_to"
                    disabled={!edit}
                    required={true}
                    showValidationError={true}
                    customErrorMessage={
                      complaint.related_to &&
                      !complaint_categories.includes(complaint.related_to)
                        ? `Please reselect previous category: ${complaint.related_to}`
                        : undefined
                    }
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Reference only — select technical category below
                  </div>
                </div>
              )}

              {deviceTokens.length === 0 && (
                <div className="flex flex-col">
                  <label
                    htmlFor="device_working_status"
                    className="mr-2 text-sm leading-7"
                  >
                    Device working status <RequiredMark />
                  </label>
                  <select
                    disabled={!edit}
                    required
                    value={complaint.device_working_status || ""}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        device_working_status: e.target.value,
                      }))
                    }
                    name="device_working_status"
                    id="device_working_status"
                    className="border px-2 py-1"
                  >
                    <option value="" disabled hidden>
                      Select
                    </option>
                    {["Working", "Partially Working", "Not Working"].map(
                      (status) => (
                        <option value={status} key={status}>
                          {status}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}

              <div className="flex flex-col">
                <label htmlFor="subject" className="mr-2">
                  Subject {!isHPCL && <RequiredMark />}
                </label>
                <input
                  disabled={!edit}
                  required={!isHPCL}
                  value={complaint.subject}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      subject: e.target.value,
                    }));
                  }}
                  className="border px-2 py-1 text-sm"
                  type="text"
                  name="subject"
                  id="subject"
                  placeholder="Maximum 90 characters"
                />
              </div>

              <div className="flex flex-col">
                <label htmlFor="description" className="mr-2 text-sm leading-7">
                  Description
                </label>
                <textarea
                  disabled={!edit}
                  required={!isHPCL}
                  rows={4}
                  value={complaint.description}
                  onChange={(e) => {
                    setComplaint((c) => ({
                      ...c,
                      description: e.target.value,
                    }));
                  }}
                  className="border px-2 py-1 text-sm"
                  name="description"
                  id="description"
                  placeholder="Detailed description of issue"
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Ticket details"
            subtitle="Ticket type, device status, and description"
          >
            <div className="space-y-6">
              {/* ROW 1 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Visibility */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">Visibility</label>
                  <select
                    disabled={!edit}
                    value={complaint.active ? "true" : "false"}
                    onChange={(e) =>
                      setComplaint((o) => ({
                        ...o,
                        active: e.target.value === "true",
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full"
                  >
                    <option value="" hidden>
                      Select
                    </option>
                    <option value="true">Active</option>
                    <option value="false">Closed</option>
                  </select>
                </div>

                {/* Status */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">
                    Status <RequiredMark />
                  </label>
                  {(() => {
                    const statusOptions = Object.values(
                      complaint.source_platform === "Home_Platform"
                        ? homeInstallationComplaintStatus
                        : complaintStatus,
                    );
                    const statusMissing =
                      complaint.status &&
                      !statusOptions.includes(complaint.status);
                    return (
                      <>
                        <select
                          disabled={!edit}
                          value={complaint.status}
                          onChange={(e) =>
                            setComplaint((c) => ({
                              ...c,
                              status: e.target.value,
                            }))
                          }
                          className="border rounded px-3 py-2 text-sm w-full bg-yellow-50"
                        >
                          <option value="" hidden>
                            Select
                          </option>
                          {statusMissing && (
                            <option value={complaint.status}>
                              {complaint.status}
                            </option>
                          )}
                          {statusOptions.map((d, i) => (
                            <option key={i} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                        {statusMissing && (
                          <p className="text-xs text-red-400">
                            Please reselect status: {complaint.status}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Complaint Date */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">
                    Date <RequiredMark />
                  </label>
                  <input
                    type="date"
                    disabled={!edit}
                    value={moment(complaint.complaint_date).format(
                      "YYYY-MM-DD",
                    )}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        complaint_date:
                          new Date(e.target.value).getTime() -
                          1000 * 60 * 60 * 5.5,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full"
                  />
                </div>

                {/* Due Date */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">
                    Due Date <RequiredMark />
                  </label>
                  <input
                    type="date"
                    disabled={!edit}
                    min={moment().format("YYYY-MM-DD")}
                    value={moment(complaint.next_action_date).format(
                      "YYYY-MM-DD",
                    )}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        next_action_date:
                          new Date(e.target.value).getTime() -
                          1000 * 60 * 60 * 5.5,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full"
                  />
                </div>
              </div>

              {/* ROW 2 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Priority */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600">Priority</label>
                  <select
                    disabled={!edit}
                    value={complaint.priority}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        priority: e.target.value,
                      }))
                    }
                    className="border rounded px-3 py-2 text-sm w-full"
                  >
                    <option value="" hidden>
                      Select
                    </option>
                    {Object.values(PriorityType).map((d, i) => (
                      <option key={i} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SOLVED SECTION */}
              {(complaint.status === "Solved" ||
                complaint.status === "Resolved" ||
                complaint.status === "Closed") && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Solved Via */}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-600">Solved via</label>
                    <select
                      disabled={!edit}
                      value={complaint.solvedVia}
                      onChange={(e) =>
                        setComplaint((c) => ({
                          ...c,
                          solvedVia: e.target.value,
                        }))
                      }
                      className="border rounded px-3 py-2 text-sm w-full"
                    >
                      <option value="" hidden>
                        Select
                      </option>
                      <option value="call">Via call</option>
                      <option value="visit">Via visit</option>
                      {!isHomeInstallation && (
                        <>
                          <option value="code-fix">Via code fix</option>
                          <option value="config-changes">
                            Via Config changes
                          </option>
                          <option value="replace-pcb">Via Replace PCB</option>
                          <option value="firmware-update">
                            Via Firmware update
                          </option>
                        </>
                      )}
                    </select>
                  </div>

                  {/* Closed Date */}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-600">Closed On</label>
                    <input
                      type="date"
                      disabled={!edit}
                      value={
                        complaint.closedDate
                          ? moment(complaint.closedDate).format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={(e) =>
                        setComplaint((c) => ({
                          ...c,
                          closedDate:
                            new Date(e.target.value).getTime() -
                            1000 * 60 * 60 * 5.5,
                        }))
                      }
                      className="border rounded px-3 py-2 text-sm w-full"
                    />
                  </div>

                  {/* Resolved At (synced from Home App, read-only) */}
                  {complaint.resolved_at && (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-gray-600">
                        Resolved At
                      </label>
                      <div className="border rounded px-3 py-2 text-sm w-full bg-gray-50">
                        {moment(complaint.resolved_at).format(
                          "DD/MM/YYYY, HH:mm",
                        )}
                      </div>
                    </div>
                  )}

                  {/* Closed At (synced from Home App, read-only) */}
                  {complaint.closed_at && (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm text-gray-600">Closed At</label>
                      <div className="border rounded px-3 py-2 text-sm w-full bg-gray-50">
                        {moment(complaint.closed_at).format(
                          "DD/MM/YYYY, HH:mm",
                        )}
                      </div>
                    </div>
                  )}

                  {/* Remarks */}
                  <div className="flex flex-col gap-1 md:col-span-3">
                    <label className="text-sm text-gray-600">Remarks</label>
                    <textarea
                      rows={3}
                      disabled={!edit}
                      value={complaint.remarks}
                      onChange={(e) =>
                        setComplaint((c) => ({
                          ...c,
                          remarks: e.target.value,
                        }))
                      }
                      className="border rounded px-3 py-2 text-sm w-full resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            title="Internal classification"
            subtitle="Hidden from customers and dealers. Full ERP technical taxonomy."
          >
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col">
                  {isHomeInstallation ? (
                    <>
                      <label
                        htmlFor="related_to_hi"
                        className="mr-2 text-sm leading-7"
                      >
                        Complaint category <RequiredMark />
                      </label>
                      <select
                        id="related_to_hi"
                        name="related_to"
                        disabled={!edit}
                        value={complaint.related_to || ""}
                        onChange={(e) =>
                          setComplaint((c: any) => ({
                            ...c,
                            related_to: e.target.value,
                            category: e.target.value,
                            subcategory_of_related_to: "",
                          }))
                        }
                        className="border px-2 py-1"
                      >
                        <option value="" disabled hidden>
                          Select
                        </option>
                        {complaint_categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <ComplaintRelatedToSearchField
                      label="Complaint category"
                      value={complaint.related_to}
                      options={complaint_categories}
                      onChange={(value) => {
                        setComplaint((c: any) => ({
                          ...c,
                          related_to: value,
                          subcategory_of_related_to: "",
                        }));
                      }}
                      onClear={() => {
                        setComplaint((c: any) => ({
                          ...c,
                          related_to: "",
                          subcategory_of_related_to: "",
                        }));
                      }}
                      id="complaint_related_to"
                      name="related_to"
                      disabled={!edit}
                      required={true}
                      showValidationError={true}
                      customErrorMessage={
                        complaint.related_to &&
                        !complaint_categories.includes(complaint.related_to)
                          ? `Please reselect previous category: ${complaint.related_to}`
                          : undefined
                      }
                    />
                  )}
                </div>

                <div className="flex flex-col">
                  {isHomeInstallation ? (
                    <>
                      <label
                        htmlFor="subcategory_hi"
                        className="mr-2 text-sm leading-7"
                      >
                        Complaint subcategory
                      </label>
                      <select
                        id="subcategory_hi"
                        name="subcategory_of_related_to"
                        disabled={!edit || subCategories.length === 0}
                        value={complaint.subcategory_of_related_to || ""}
                        onChange={(e) =>
                          setComplaint((c: any) => ({
                            ...c,
                            subcategory_of_related_to: e.target.value,
                            category: e.target.value,
                          }))
                        }
                        className="border px-2 py-1"
                      >
                        <option value="" disabled hidden>
                          Select
                        </option>
                        {subCategories.map((sub) => (
                          <option key={sub} value={sub}>
                            {sub}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <ComplaintRelatedToSearchField
                      label="Complaint subcategory"
                      value={complaint.subcategory_of_related_to || ""}
                      options={subCategories}
                      onChange={(value) => {
                        setComplaint((c: any) => ({
                          ...c,
                          subcategory_of_related_to: value,
                        }));
                      }}
                      id="subcategory_of_related_to"
                      name="subcategory_of_related_to"
                      disabled={!edit || subCategories.length === 0}
                      required={isSubCategoryRequired}
                      showValidationError={true}
                      customErrorMessage={
                        isSubCategoryRequired &&
                        complaint.subcategory_of_related_to &&
                        !subCategories.includes(
                          complaint.subcategory_of_related_to,
                        )
                          ? `Please reselect previous sub-category: ${complaint.subcategory_of_related_to}`
                          : undefined
                      }
                    />
                  )}
                </div>

                <div className="flex flex-col">
                  <label htmlFor="severity" className="mr-2 text-sm leading-7">
                    Severity
                  </label>
                  <select
                    disabled={!edit}
                    value={complaint.severity || PriorityType.medium}
                    onChange={(e) => {
                      setComplaint((c) => ({
                        ...c,
                        severity: e.target.value,
                      }));
                    }}
                    name="severity"
                    id="severity"
                    className="border px-2 py-1"
                  >
                    {Object.values(PriorityType).map((val, i) => (
                      <option value={val} key={i}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="complaint_detail_safety_flag_checkbox"
                    type="checkbox"
                    checked={!!complaint.safety_flag}
                    disabled={!edit}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        safety_flag: e.target.checked,
                      }))
                    }
                  />
                  Safety risk flag
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id="complaint_detail_rca_required_checkbox"
                    type="checkbox"
                    checked={!!complaint.rca_required}
                    disabled={!edit}
                    onChange={(e) =>
                      setComplaint((c) => ({
                        ...c,
                        rca_required: e.target.checked,
                      }))
                    }
                  />
                  RCA required
                </label>
              </div>
            </div>
          </SectionCard>
        </>

        {complaint.id && (
          <div className="flex my-5 gap-3">
            <button
              id="complaint_detail_comment_btn"
              type="button"
              onClick={() => setShowComments(true)}
              className="border rounded px-2 py-1"
            >
              Comment
            </button>
            <button
              id="complaint_detail_history_btn"
              type="button"
              onClick={() => setShowLogs(true)}
              className="border rounded px-2 py-1"
            >
              History
            </button>
            <button
              id="complaint_detail_attachments_btn"
              type="button"
              onClick={() => setShowAttachments(true)}
              className="rounded border px-3 py-1 hover:bg-black hover:text-white"
            >
              Attachments
            </button>
            {!disabled && (
              <button
                id="complaint_detail_edit_action_btn"
                type="button"
                onClick={() => setEdit(true)}
                className={
                  "rounded border px-3 py-1 hover:bg-black hover:text-white " +
                  (edit ? "hidden" : "block")
                }
              >
                Edit
              </button>
            )}
            <ReplacementPipelineWidget
              complaintId={complaint.id}
              complaintStatus={complaint.status}
              canRaise={!disabled}
            />
          </div>
        )}

        {complaint.id && (
          <p className="flex flex-wrap gap-5 text-sm my-3">
            <span>
              Complaint by :<b> {complaint.complaint_by || "--"}</b>
            </span>
            <span>
              Created by :<b> {complaint.created_by_name || "--"}</b>
              <span className="text-xs mx-2">
                {moment(complaint.created_at).format("D MMM YYYY HH:mm a")}
              </span>
            </span>
            <span>
              Updated by :<b> {complaint.updated_by_name || "--"}</b>
              <span className="text-xs mx-2">
                {moment(complaint.updated_at).format("D MMM YYYY HH:mm a")}
              </span>
            </span>
          </p>
        )}

        {edit && (
          <div className="flex justify-center gap-3 sm:gap-5 fixed bottom-0 left-0 lg:left-60 right-0 z-40 bg-white p-3 border-t border-gray-200 shadow-[0_-4px_10px_rgba(0,0,0,0.12)]">
            {complaint.id && (
              <button
                id="complaint_detail_cancel_edit_btn"
                onClick={() => {
                  setEdit(false);
                  setComplaint(preVal);
                  setDeviceIdVerified(null);
                  setDeviceIdVerifyMsg("");
                }}
                type="button"
                className="border bg-gray-100 flex gap-2 hover:bg-black hover:text-white rounded items-center px-3 py-1"
              >
                Cancel
              </button>
            )}
            <button
              id="complaint_detail_submit_btn"
              className="border bg-gray-100 flex gap-2 hover:bg-black hover:text-white rounded items-center px-3 py-1"
            >
              Update
            </button>
          </div>
        )}
      </form>
      <hr />

      <LoadingWidget loading={loading} />
    </div>
  );
};

export default ComplainantDetailPage;
